import { UserStatus, Role, InvitationStatus } from '@/types/domain/user';

export interface UserStatusMetadata {
  label: string;
  badgeVariant: 'default' | 'outline' | 'secondary' | 'success' | 'destructive' | 'warning' | 'info';
  dotColor: string;
  textColor: string;
  bgColor: string;
  description: string;
  allowedTransitions: UserStatus[];
}

export const USER_STATUS_CONFIG: Record<UserStatus, UserStatusMetadata> = {
  INVITATION_PENDING: {
    label: 'Invitation Pending',
    badgeVariant: 'warning',
    dotColor: 'bg-amber-500',
    textColor: 'text-amber-600 dark:text-amber-400',
    bgColor: 'bg-amber-500/10 border-amber-500/20',
    description: 'Onboarding invitation dispatched; awaiting initial employee activation.',
    allowedTransitions: ['ACTIVE', 'INACTIVE', 'BLOCKED'],
  },
  ACTIVE: {
    label: 'Active',
    badgeVariant: 'success',
    dotColor: 'bg-emerald-500',
    textColor: 'text-emerald-600 dark:text-emerald-400',
    bgColor: 'bg-emerald-500/10 border-emerald-500/20',
    description: 'Full enterprise authorization to access assigned modules and authority levels.',
    allowedTransitions: ['INACTIVE', 'BLOCKED', 'SUSPENDED', 'LOCKED'],
  },
  INACTIVE: {
    label: 'Inactive',
    badgeVariant: 'secondary',
    dotColor: 'bg-slate-400',
    textColor: 'text-slate-600 dark:text-slate-400',
    bgColor: 'bg-slate-500/10 border-slate-500/20',
    description: 'Account administratively deactivated; login access revoked.',
    allowedTransitions: ['ACTIVE', 'BLOCKED'],
  },
  BLOCKED: {
    label: 'Blocked',
    badgeVariant: 'destructive',
    dotColor: 'bg-rose-600',
    textColor: 'text-rose-600 dark:text-rose-400',
    bgColor: 'bg-rose-500/10 border-rose-500/20',
    description: 'Security containment applied; identity quarantined from all BAPI/ERP services.',
    allowedTransitions: ['ACTIVE', 'INACTIVE'],
  },
  SUSPENDED: {
    label: 'Suspended',
    badgeVariant: 'warning',
    dotColor: 'bg-amber-600',
    textColor: 'text-amber-700 dark:text-amber-300',
    bgColor: 'bg-amber-600/10 border-amber-600/20',
    description: 'Temporary HR or compliance review hold; transaction release rights paused.',
    allowedTransitions: ['ACTIVE', 'INACTIVE', 'BLOCKED'],
  },
  LOCKED: {
    label: 'Locked',
    badgeVariant: 'destructive',
    dotColor: 'bg-rose-500',
    textColor: 'text-rose-600 dark:text-rose-400',
    bgColor: 'bg-rose-500/10 border-rose-500/20',
    description: 'Triggered automatically after 5 consecutive failed authentications; unlockable by admin.',
    allowedTransitions: ['ACTIVE', 'INACTIVE', 'BLOCKED'],
  },
};

export const INVITATION_STATUS_CONFIG: Record<
  InvitationStatus,
  { label: string; badgeVariant: 'warning' | 'success' | 'destructive'; dotColor: string }
> = {
  INVITATION_PENDING: {
    label: 'Pending',
    badgeVariant: 'warning',
    dotColor: 'bg-amber-500',
  },
  ACCEPTED: {
    label: 'Accepted',
    badgeVariant: 'success',
    dotColor: 'bg-emerald-500',
  },
  EXPIRED: {
    label: 'Expired',
    badgeVariant: 'destructive',
    dotColor: 'bg-rose-500',
  },
};

export const ROLE_METADATA: Record<
  Role,
  {
    title: string;
    department: string;
    description: string;
    defaultLimit: number;
    badgeVariant: 'default' | 'outline' | 'secondary' | 'info';
    isSystemControlled?: boolean;
    isReadOnly?: boolean;
  }
> = {
  EMPLOYEE: {
    title: 'Employee / Requisitioner',
    department: 'All Business Units',
    description: 'Initiate purchase requisitions, request materials and track personal items.',
    defaultLimit: 10000,
    badgeVariant: 'outline',
  },
  MANAGER: {
    title: 'Department Manager',
    department: 'Departmental Leadership',
    description: 'Review and approve department requisitions up to authority limit.',
    defaultLimit: 50000,
    badgeVariant: 'info',
  },
  PROCUREMENT: {
    title: 'Procurement Officer',
    department: 'Strategic Sourcing',
    description: 'Generate RFQs, evaluate vendor bids, negotiate contracts, and issue POs.',
    defaultLimit: 250000,
    badgeVariant: 'default',
  },
  PROCUREMENT_MANAGER: {
    title: 'Procurement Manager',
    department: 'Strategic Sourcing',
    description: 'Sign off high-value sourcing awards, approve PO releases and supplier contracts.',
    defaultLimit: 1000000,
    badgeVariant: 'default',
  },
  FINANCE: {
    title: 'Finance Approver',
    department: 'Accounts Payable & Treasury',
    description: 'Perform 3-way invoice reconciliation, approve disbursements and manage tax audits.',
    defaultLimit: 500000,
    badgeVariant: 'default',
  },
  NFA_LFA_APPROVER: {
    title: 'NFA/LFA Committee Approver',
    department: 'Executive Steering Committee',
    description: 'Sign commercial Notes for Approval and formal Letters for Award.',
    defaultLimit: 5000000,
    badgeVariant: 'default',
  },
  MANAGEMENT: {
    title: 'Senior Management / CPO',
    department: 'Executive Leadership',
    description: 'Oversee corporate spend strategy, cross-plant budgets, and sovereign approvals.',
    defaultLimit: 10000000,
    badgeVariant: 'default',
  },
  SAP_INTEGRATION: {
    title: 'SAP Integration Identity',
    department: 'Enterprise IT',
    description: 'System identity reserved for RFC/BAPI synchronization with SAP S/4HANA.',
    defaultLimit: 0,
    badgeVariant: 'secondary',
    isSystemControlled: true,
  },
  ADMIN: {
    title: 'System Administrator',
    department: 'IT Infrastructure',
    description: 'Complete administrative governance, user provisioning, security policies, and RBAC.',
    defaultLimit: 99999999,
    badgeVariant: 'default',
  },
  AUDITOR: {
    title: 'Internal Compliance Auditor',
    department: 'Internal Audit & Vigilance',
    description: 'Strict read-only oversight across all transactional ledgers and audit trails.',
    defaultLimit: 0,
    badgeVariant: 'outline',
    isReadOnly: true,
  },
};

export const ENTERPRISE_DEPARTMENTS = [
  { code: 'DEP-IT', name: 'Information Technology', costCenter: 'CC-1010' },
  { code: 'DEP-PROC', name: 'Global Sourcing & Procurement', costCenter: 'CC-1020' },
  { code: 'DEP-FIN', name: 'Enterprise Finance & Treasury', costCenter: 'CC-1030' },
  { code: 'DEP-OPS', name: 'Manufacturing Operations', costCenter: 'CC-1040' },
  { code: 'DEP-SCM', name: 'Supply Chain & Logistics', costCenter: 'CC-1050' },
  { code: 'DEP-EXEC', name: 'Executive Steering Committee', costCenter: 'CC-1000' },
  { code: 'DEP-AUDIT', name: 'Compliance & Internal Audit', costCenter: 'CC-1060' },
];

export const ENTERPRISE_PLANTS = [
  { code: 'PLANT-1000', name: 'Plant 1000 — Mumbai Central Hub', companyCode: '1000', location: 'Mumbai, MH' },
  { code: 'PLANT-1020', name: 'Plant 1020 — Gurugram Tech Facility', companyCode: '1000', location: 'Gurugram, HR' },
  { code: 'PLANT-1030', name: 'Plant 1030 — Bengaluru R&D Campus', companyCode: '1000', location: 'Bengaluru, KA' },
  { code: 'PLANT-1040', name: 'Plant 1040 — Chennai Heavy Fabrication', companyCode: '1000', location: 'Chennai, TN' },
  { code: 'PLANT-1050', name: 'Plant 1050 — Pune Assembly Plant', companyCode: '1000', location: 'Pune, MH' },
  { code: 'PLANT-2000', name: 'Plant 2000 — Frankfurt Innovation Center', companyCode: '2000', location: 'Frankfurt, DE' },
  { code: 'PLANT-3000', name: 'Plant 3000 — Singapore Logistics Hub', companyCode: '3000', location: 'Singapore, SG' },
  { code: 'PLANT-4000', name: 'Plant 4000 — Hyderabad Sourcing Hub', companyCode: '4000', location: 'Hyderabad, IN' },
  { code: 'PLANT-5000', name: 'Plant 5000 — Chicago Manufacturing Plant', companyCode: '1000', location: 'Chicago, IL' },
  { code: 'PLANT-6000', name: 'Plant 6000 — Munich Assembling Facility', companyCode: '2000', location: 'Munich, DE' },
];

export const DESIGNATION_OPTIONS = [
  'Senior Procurement Specialist',
  'Lead Sourcing Category Manager',
  'Procurement Analyst',
  'Department General Manager',
  'Assistant Vice President',
  'VP of Global Supply Chain',
  'Finance Controller',
  'Senior Accounts Payable Specialist',
  'Treasury Lead',
  'IT Systems Administrator',
  'Principal Enterprise Architect',
  'Internal Auditor & Vigilance Lead',
  'Requisitioner / Project Engineer',
  'Commercial Contracts Director',
];
